import { useEffect, useState } from "react";
import {
  DocumentDetailsContract,
  DocumentDetailsOthers,
} from "../../../../api/services/client/models";
import { ReactComponent as DocumentsIcon } from "../../../../assets/images/documents.svg";
import { ReactComponent as UploadIcon } from "../../../../assets/images/upload.svg";
import {
  ITabs,
  MuiBox,
  MuiGrid,
  MuiTabPanel,
  MuiTabs,
} from "../../../../components";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import {
  CLIENT_DOCUMENT_TYPES,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ClientDocument, DocumentType } from "../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ArchiveDocumentList } from "./archiveClientDocumentList/ArchiveDocumentList";
import { DocumentList } from "./createClientDocumentList/DocumentList";
import { CreateClientDocumentsWrapper } from "./CreateClientDocuments.style";
import { OtherDocumentList } from "./createClientOtherDocumentList/OtherDocumentList";
import { usePermissions } from "../../../../hooks/usePermissions";

export const DOCUMENT_TABS_INDEX = {
  CONTRACT: 0,
  OTHERS: 1,
  ARCHIVE: 2,
};

type CreateClientDocumentsProps = {
  onDocumentView?: (
    data: DocumentDetailsOthers | DocumentDetailsContract,
  ) => void;
  otherDocuments?: ClientDocument[];
  contractDocuments?: ClientDocument[];
  archiveDocuments?: ClientDocument[];
  onDocumentUploaded: (
    type?: DocumentType,
    isAllowToNavigate?: boolean,
  ) => void;
  onDocumentEdit: (documentId: string, documentType: string) => void;
  onDocumentTabChange: (value: number) => void;
  selectDocumentTab?: number;
  isEditable?: boolean;
  onUploadDocumentClick: () => void;
  onInheritDocumentClick: () => void;
};

export const CreateClientDocuments: React.FC<CreateClientDocumentsProps> = ({
  onDocumentView,
  otherDocuments,
  contractDocuments,
  archiveDocuments,
  onDocumentEdit,
  onDocumentUploaded,
  onDocumentTabChange,
  isEditable = true,
  selectDocumentTab = DOCUMENT_TABS_INDEX.CONTRACT,
  onUploadDocumentClick,
  onInheritDocumentClick,
}) => {
  const [selectedTab, setSelectedTab] = useState(selectDocumentTab);

  const { fetchLabel } = useSiteCore();
  const { hasModulePermission } = usePermissions();

  const getLabel = (key: string) => {
    return fetchLabel(documentSiteCoreKeys.documentsTitle, key);
  };

  const tabIdContract = `tab-contract`;
  const tabIdOthers = `tab-others`;
  const tabIdArchive = `tab-archive`;

  const tabs: ITabs[] = [
    { id: tabIdContract, label: getLabel(documentSiteCoreKeys.contractTab) },
    { id: tabIdOthers, label: getLabel(documentSiteCoreKeys.othersTab) },
    { id: tabIdArchive, label: getLabel(documentSiteCoreKeys.archiveTab) },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
    onDocumentTabChange(value);
  };

  useEffect(() => {
    setSelectedTab(selectDocumentTab);
  }, [selectDocumentTab]);

  return (
    <CreateClientDocumentsWrapper>
      <MuiTabs
        id="documents"
        tabsTestIds={[tabIdContract, tabIdOthers]}
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
      >
        {isEditable &&
          hasModulePermission(MODULE_PERMISSIONS.ADD_CLIENT_DOCUMENTS) && (
            <MuiBox className="semi-header">
              <ButtonWithIcon
                buttonText={getLabel(documentSiteCoreKeys.inheritDocument)}
                onActionClick={onInheritDocumentClick}
                ActionIcon={DocumentsIcon}
              />
              <ButtonWithIcon
                buttonText={getLabel(documentSiteCoreKeys.uploadNewDocument)}
                onActionClick={onUploadDocumentClick}
                ActionIcon={UploadIcon}
              />
            </MuiBox>
          )}
        <MuiTabPanel
          panelId="panel1"
          value={selectedTab}
          index={0}
          ariaLabelledById={tabIdContract}
        >
          <MuiGrid container data-testid="contract-documents">
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <DocumentList
                rows={contractDocuments || []}
                onDocumentView={onDocumentView}
                onDocumentEdit={onDocumentEdit}
                successHandler={() =>
                  onDocumentUploaded(
                    CLIENT_DOCUMENT_TYPES.clientContract as DocumentType,
                  )
                }
                isEditable={isEditable}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiTabPanel>

        <MuiTabPanel
          panelId="panel2"
          value={selectedTab}
          index={1}
          ariaLabelledById={tabIdOthers}
        >
          <MuiGrid container data-testid="other-documents">
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <OtherDocumentList
                rows={otherDocuments || []}
                onDocumentEdit={onDocumentEdit}
                onDocumentView={onDocumentView}
                successHandler={onDocumentUploaded}
                isEditable={isEditable}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiTabPanel>
        <MuiTabPanel
          panelId="panel2"
          value={selectedTab}
          index={2}
          ariaLabelledById={tabIdOthers}
        >
          <MuiGrid container data-testid="archive-documents">
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <ArchiveDocumentList
                rows={archiveDocuments || []}
                onDocumentEdit={onDocumentEdit}
                onDocumentView={onDocumentView}
                successHandler={() => onDocumentUploaded(undefined, false)}
                isEditable={isEditable}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiTabPanel>
      </MuiTabs>
    </CreateClientDocumentsWrapper>
  );
};
